import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import React, { useEffect } from 'react'
import {
  useAccountPropertiesMutation,
  useSurveyMutation,
  useTrackMutation
} from '@mote/api'
import { TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import type { Answer, Role, SchoolEntryType, SurveyKey } from '../typings'
import { OnboardingRoles } from '../typings'

export interface OnboardingSurveyState {
  role: Role | null
  acceptedTerms: boolean
  hasAdultPermission: boolean
  newsletterConsent: boolean
  schoolNameEntryType: SchoolEntryType
  surveyKey: SurveyKey
  survey: Partial<Record<SurveyKey, Answer>>
}

export interface OnboardingContextValues {
  state: OnboardingSurveyState
  isLoading: boolean
  updateState: (state: Partial<OnboardingSurveyState>) => void
  resetState: () => void
  setIsLoading: Dispatch<SetStateAction<boolean>>
  toggleLoading: () => void
  updateProperties: (payload: { payload: Record<string, any> }) => Promise<void>
  postSurvey: () => Promise<void>
  isValidState: () => boolean
  getConsentStatus: () => boolean
  institutionNameHints: string[]
  canSchoolSearchDuringUserOnboarding: boolean
}

export const OnboardingContext =
  React.createContext<OnboardingContextValues | null>(null)

function getDefaultState(): OnboardingSurveyState {
  return {
    role: null,
    acceptedTerms: false,
    hasAdultPermission: false,
    newsletterConsent: false,
    surveyKey: 'role',
    schoolNameEntryType: 'manual',
    survey: {}
  }
}

function isValidValue(value: any): boolean {
  return (
    value !== undefined &&
    value !== null &&
    ((typeof value === 'object' && Object.keys(value).length > 0) ||
      value !== '')
  )
}

type SurveyContext = 'full-page' | 'modal'

export interface OnboardingProviderProps {
  institutionNameHints: string[]
  surveyContext: SurveyContext
  canSchoolSearchDuringUserOnboarding: boolean
}

export function OnboardingProvider(
  props: PropsWithChildren<OnboardingProviderProps>
): JSX.Element {
  const accountPropertiesMutation = useAccountPropertiesMutation()
  const surveyMutation = useSurveyMutation()
  const trackMutation = useTrackMutation()

  useEffect(() => {
    trackMutation.mutate!({
      variables: {
        event: TRACK_EVENT_NAMES.onboarding.started,
        surveyContext: props.surveyContext
      }
    })
  }, [])

  const [state, setState] = React.useState<OnboardingSurveyState>(
    getDefaultState()
  )
  const [isLoading, setIsLoading] = React.useState(false)

  const updateState = (states: Partial<OnboardingSurveyState>) => {
    setState(prev => ({ ...prev, ...states }))
  }

  const toggleLoading = () => {
    setIsLoading(prev => !prev)
  }

  const resetState = () => {
    setState(getDefaultState())
  }

  const updateProperties = ({ payload = {} }) => {
    return accountPropertiesMutation.mutate!({ variables: payload })
  }

  const postSurvey = React.useCallback(() => {
    return surveyMutation.mutate!({
      variables: { survey: state.survey }
    })
      .then(() => {
        if (state.role !== OnboardingRoles.educator) return

        return trackMutation.mutate!({
          variables: {
            event: TRACK_EVENT_NAMES.onboarding.schoolNameEntered,
            schoolNameEntryType: state.schoolNameEntryType
          }
        })
      })
      .then(() => {
        return trackMutation.mutate!({
          variables: {
            event: TRACK_EVENT_NAMES.onboarding.surveyComplete,
            schoolNameEntryType: undefined, // TODO: find why this remains populated from the previous call
            surveyContext: props.surveyContext,
            role: state.role
          }
        })
      })
  }, [state, props.surveyContext])

  const getConsentStatus = React.useCallback(() => {
    const isMinor = Boolean(state.survey.is_minor?.value)
    return (
      (!isMinor && state.acceptedTerms) || (isMinor && state.hasAdultPermission)
    )
  }, [state])

  const isValidState = React.useCallback(() => {
    const key = state.surveyKey
    const mainCondition = isValidValue(state.survey[key]?.value)

    return mainCondition && getConsentStatus()
  }, [state, getConsentStatus])

  return (
    <OnboardingContext.Provider
      value={{
        state,
        isLoading,
        updateState,
        resetState,
        setIsLoading,
        toggleLoading,
        updateProperties,
        postSurvey,
        isValidState,
        getConsentStatus,
        institutionNameHints: props.institutionNameHints,
        canSchoolSearchDuringUserOnboarding: false
      }}
    >
      {props.children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = (): OnboardingContextValues => {
  const context = React.useContext(OnboardingContext)

  if (context === null) {
    throw new Error(
      'Attempted to call useOnboarding outside the Onboarding component.'
    )
  }

  return context
}
